import { useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { minPlanFor, seatLimit } from '@dale/plans';

import { ApiError } from '../api/client';
import { listBookings } from '../api/endpoints/bookings';
import { createStaff, listStaff, patchStaff } from '../api/endpoints/staff';
import {
  createAccess,
  listAccess,
  resetAccessPassword,
  setAccessActive,
  setAccessProfile,
  setAccessStaffMember,
} from '../api/endpoints/users';
import { createIdempotencyKeyHolder } from '../api/idempotency';
import type { AccessProfile, Booking, BusinessAccessRecord, BusinessStaffRecord, Role } from '../api/schemas';
import { insideNativeApp } from '../auth/native-app';
import { isOwner } from '../auth/roles';
import { useSession } from '../auth/useSession';
import { ConfirmAction } from '../components/ConfirmAction';
import { EmptyState } from '../components/EmptyState';
import { ErrorBanner } from '../components/ErrorBanner';
import { FormField } from '../components/FormField';
import { LoadingState } from '../components/LoadingState';
import { Notice } from '../components/Notice';
import { PageHeader } from '../components/PageHeader';
import { LockedSection } from '../components/PlanLocked';
import { StaffServices } from '../components/StaffServices';
import { DEFAULT_PROFILE, PROFILE_BLURB, PROFILE_LABEL, profilesForMode } from '../features/permissions/catalog';
import { PermisosSection } from '../features/permissions/PermisosSection';
import { bookingsQueryKey, upcomingWindow } from '../lib/bookingWindows';
import { generateTemporaryPassword } from '../lib/generatePassword';
import { modeForCategory, type PortalMode } from '../lib/mode';
import { meetsPlan, PLAN_ORDER, planLabel, type PlanTier } from '../lib/plan';
import { useBusiness } from '../lib/useBusiness';
import { useMutationAttempt } from '../lib/use-mutation-attempt';

const STAFF_QUERY_KEY = ['business-staff'];
const ACCESS_QUERY_KEY = ['business-access'];

/** Acceso role word (docs/CONCEPTS.md): distinct from the generic ROLE_LABEL used elsewhere. */
const ACCESS_ROLE_LABEL: Record<Role, string> = {
  owner: 'Titular',
  employee: 'Equipo',
};

/** The create form offers the profiles a business uses plus a second titular; employees default to Recepción. */
type NewAccessKind = AccessProfile | 'owner';

/** An employee's profile, read the way the API reads a row that has none yet. */
function profileOf(access: BusinessAccessRecord): AccessProfile {
  return access.accessProfile ?? (access.staffMemberId ? 'coach' : DEFAULT_PROFILE);
}

/** The word a row shows for who this access is: Titular, or the employee's profile. */
function accessKindLabel(access: BusinessAccessRecord): string {
  if (access.role === 'owner') return ACCESS_ROLE_LABEL.owner;
  return PROFILE_LABEL[profileOf(access)];
}

export function EquipoPage() {
  const { session } = useSession();
  const owner = isOwner(session?.role);
  // Staff and their logins are Base (three logins, the owner's included); what each profile sees is Pro.
  const business = useBusiness();
  const plan = business?.plan;
  const mode = modeForCategory(business?.category ?? '');
  // Only once the plan is known: before it, a Pro business would read its logins against Base's three.
  const managesAccess = owner && business !== undefined && meetsPlan(plan, minPlanFor('accesos'));

  const queryClient = useQueryClient();
  const staffQuery = useQuery({ queryKey: STAFF_QUERY_KEY, queryFn: () => listStaff(true) });

  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [serviceIds, setServiceIds] = useState<string[] | undefined>();
  const createMutation = useMutationAttempt({
    mutationFn: createStaff,
    exito: 'Persona agregada',
    onSuccess: () => {
      setName('');
      setRole('');
      setServiceIds(undefined);
      void queryClient.invalidateQueries({ queryKey: STAFF_QUERY_KEY });
    },
  });

  return (
    <main>
      <PageHeader title="Equipo" description="Quién trabaja en tu negocio." />
      {staffQuery.isLoading ? <LoadingState label="Cargando equipo…" /> : null}
      {staffQuery.isError ? <ErrorBanner message={errorMessage(staffQuery.error)} /> : null}
      {staffQuery.data ? (
        staffQuery.data.data.length === 0 ? (
          <EmptyState
            title="Todavía no tienes personas en el equipo."
            description="Tus clientes eligen con quién reservar por nombre."
          />
        ) : (
          <ul className="rows">
            {staffQuery.data.data.map((member) => (
              <li key={member.id}>
                <StaffRow member={member} />
              </li>
            ))}
          </ul>
        )
      ) : null}

      <section className="section" aria-label="Agregar persona">
        <h2 className="section-label">Agregar persona</h2>
        <form
          className="card form-grid cols-2"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            if (createMutation.uncertain) { createMutation.retry(); return; }
            if (serviceIds?.length === 0) return;
            createMutation.mutate({ name, role, ...(serviceIds ? { serviceIds } : {}) });
          }}
        >
          <FormField label="Nombre" htmlFor="staff-name">
            <input id="staff-name" value={name} onChange={(event) => setName(event.target.value)} required />
          </FormField>
          <FormField label="Rol" htmlFor="staff-role">
            <input id="staff-role" value={role} onChange={(event) => setRole(event.target.value)} required />
          </FormField>
          <StaffServices value={serviceIds} onChange={setServiceIds} disabled={createMutation.isPending || createMutation.uncertain} />
          {createMutation.isError ? <ErrorBanner message={errorMessage(createMutation.error)} /> : null}
          <button type={createMutation.uncertain ? "button" : "submit"} onClick={createMutation.uncertain ? createMutation.retry : undefined} className="btn" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Agregando…' : createMutation.uncertain ? 'Reintentar guardado' : 'Agregar persona'}
          </button>
        </form>
      </section>

      {managesAccess ? (
        <AccesosSection
          currentUserId={session?.businessUserId}
          mode={mode}
          plan={plan ?? 'base'}
          roster={(staffQuery.data?.data ?? []).filter((member) => member.isActive)}
        />
      ) : null}
    </main>
  );
}

type StaffRowProps = { member: BusinessStaffRecord };

function StaffRow({ member }: StaffRowProps) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(member.name);
  const [role, setRole] = useState(member.role);
  const [serviceIds, setServiceIds] = useState(member.serviceIds);
  const toggleKey = useRef(createIdempotencyKeyHolder()).current;

  const renameMutation = useMutationAttempt({
    mutationFn: (input: { name: string; role: string; serviceIds: string[] | null }, key: string) => patchStaff(member.id, input, key),
    onSuccess: () => {
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: STAFF_QUERY_KEY });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: () => patchStaff(member.id, { isActive: !member.isActive }, toggleKey.ensure()),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: STAFF_QUERY_KEY }),
    onSettled: () => toggleKey.reset(),
  });

  const [planningVacation, setPlanningVacation] = useState(false);
  const awayKey = useRef(createIdempotencyKeyHolder()).current;
  const awayMutation = useMutation({
    mutationFn: (awayUntil: string | null) => patchStaff(member.id, { awayUntil }, awayKey.ensure()),
    onSuccess: () => {
      setPlanningVacation(false);
      void queryClient.invalidateQueries({ queryKey: STAFF_QUERY_KEY });
    },
    onSettled: () => awayKey.reset(),
  });

  return (
    <div className="row">
      {editing ? (
        <form
          className="form-grid cols-2 grow"
          onSubmit={(event) => {
            event.preventDefault();
            if (renameMutation.uncertain) { renameMutation.retry(); return; }
            if (serviceIds?.length === 0) return;
            renameMutation.mutate({ name, role, serviceIds: serviceIds ?? null });
          }}
        >
          <FormField label="Nombre" htmlFor={`rename-${member.id}`}>
            <input
              id={`rename-${member.id}`}
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
            />
          </FormField>
          <FormField label="Rol" htmlFor={`role-${member.id}`}>
            <input id={`role-${member.id}`} value={role} onChange={event => setRole(event.target.value)} required />
          </FormField>
          <StaffServices value={serviceIds} onChange={setServiceIds} disabled={renameMutation.isPending || renameMutation.uncertain} />
          <button type={renameMutation.uncertain ? "button" : "submit"} onClick={renameMutation.uncertain ? renameMutation.retry : undefined} className="btn small" disabled={renameMutation.isPending}>
            {renameMutation.uncertain ? 'Reintentar guardado' : 'Guardar'}
          </button>
          <button type="button" className="btn small ghost" disabled={renameMutation.isPending || renameMutation.uncertain} onClick={() => setEditing(false)}>
            Cancelar
          </button>
        </form>
      ) : (
        <>
          <div className="grow">
            <p className="row-title">
              {member.name}{' '}
              {member.awayUntil ? (
                <span className="status" data-tone="hold">
                  De vacaciones
                </span>
              ) : member.isActive ? null : (
                <span className="status" data-tone="off">
                  Inactivo
                </span>
              )}
            </p>
            <p className="row-sub">
              {member.awayUntil ? `${member.role} · vuelve el ${returnDayLabel(member.awayUntil)}` : member.role}
            </p>
            {planningVacation ? (
              <VacationForm
                staffId={member.id}
                name={member.name}
                pending={awayMutation.isPending}
                onSave={(awayUntil) => awayMutation.mutate(awayUntil)}
                onCancel={() => setPlanningVacation(false)}
              />
            ) : null}
          </div>
          <span className="action-pair">
            {member.awayUntil ? (
              <button
                type="button"
                className="btn small secondary"
                disabled={awayMutation.isPending}
                onClick={() => awayMutation.mutate(null)}
              >
                Ya volvió
              </button>
            ) : member.isActive && !planningVacation ? (
              <button type="button" className="btn small ghost" onClick={() => setPlanningVacation(true)}>
                Vacaciones
              </button>
            ) : null}
            <button type="button" className="btn small ghost" aria-label={`Editar persona ${member.name}`} onClick={() => { setName(member.name); setRole(member.role); setServiceIds(member.serviceIds); setEditing(true); }}>
              Editar
            </button>
            <button
              type="button"
              className="btn small secondary"
              onClick={() => toggleMutation.mutate()}
              disabled={toggleMutation.isPending}
            >
              {member.isActive ? 'Desactivar' : 'Activar'}
            </button>
          </span>
        </>
      )}
      {renameMutation.isError ? <ErrorBanner message={errorMessage(renameMutation.error)} /> : null}
      {toggleMutation.isError ? <ErrorBanner message={errorMessage(toggleMutation.error)} /> : null}
      {awayMutation.isError ? <ErrorBanner message={errorMessage(awayMutation.error)} /> : null}
    </div>
  );
}

/**
 * Vacation mode: pick the day they come back. Until then they are off every
 * booking surface, and they return on their own that morning. Visits already
 * booked in between stay put, so the form says how many there are.
 */
function VacationForm({
  staffId,
  name,
  pending,
  onSave,
  onCancel,
}: {
  staffId: string;
  name: string;
  pending: boolean;
  onSave: (awayUntil: string) => void;
  onCancel: () => void;
}) {
  const [returnDay, setReturnDay] = useState('');
  const bookingsRange = upcomingWindow(new Date());
  const bookingsQuery = useQuery({
    queryKey: bookingsQueryKey(bookingsRange),
    queryFn: () => listBookings(bookingsRange),
  });
  const inputId = `vacation-${staffId}`;
  const booked = returnDay ? bookedBefore(bookingsQuery.data?.data ?? [], staffId, returnDay) : 0;
  return (
    <form
      className="vacation-form"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        if (returnDay) onSave(returnDay);
      }}
    >
      <label htmlFor={inputId} className="row-sub">
        Vuelve el
      </label>
      <input id={inputId} type="date" min={tomorrowYmd()} value={returnDay} onChange={(event) => setReturnDay(event.target.value)} required />
      <button type="submit" className="btn small" disabled={pending || !returnDay}>
        {pending ? 'Guardando…' : 'Guardar vacaciones'}
      </button>
      <button type="button" className="btn small ghost" disabled={pending} onClick={onCancel}>
        Cancelar
      </button>
      {booked > 0 ? (
        <p className="row-sub vacation-note">
          {name} tiene {booked} {booked === 1 ? 'cita' : 'citas'} antes de volver. Siguen en la agenda: muévelas o avísales.
        </p>
      ) : null}
    </form>
  );
}

/** Confirmed visits with this person from now until the morning they return. */
function bookedBefore(bookings: Booking[], staffId: string, returnDay: string): number {
  const now = Date.now();
  const back = new Date(`${returnDay}T00:00:00`).getTime();
  return bookings.filter((booking) => {
    const starts = Date.parse(booking.startsAt);
    return booking.staffId === staffId && booking.status === 'confirmed' && starts >= now && starts < back;
  }).length;
}

function tomorrowYmd(): string {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toLocaleDateString('en-CA');
}

function returnDayLabel(ymd: string): string {
  return new Date(`${ymd}T12:00:00`).toLocaleDateString('es-PA', { weekday: 'long', day: 'numeric', month: 'long' });
}

type RosterOption = { id: string; name: string };

type AccesosSectionProps = {
  currentUserId?: string;
  /** What kind of business this is: a waiter is only offered to a restaurant. */
  mode: PortalMode;
  /** The plan decides how many logins fit and whether profiles can be tuned. */
  plan: PlanTier;
  /** Active roster entries a team login can work as. */
  roster: RosterOption[];
};

/**
 * Accesos: login accounts, a deliberately different object from Miembro del
 * equipo above (docs/CONCEPTS.md). Owner-only; EquipoPage only mounts this
 * when the signed-in role is owner, so an employee never sees it even if
 * this component were rendered directly in a test.
 */
function AccesosSection({ currentUserId, mode, plan, roster }: AccesosSectionProps) {
  const queryClient = useQueryClient();
  const accessQuery = useQuery({ queryKey: ACCESS_QUERY_KEY, queryFn: listAccess });
  // Every active login takes a seat, the owner's included; the server refuses one past the plan's limit.
  const seats = seatLimit(plan);
  const taken = accessQuery.data?.data.filter((access) => access.isActive).length;
  const full = taken !== undefined && taken >= seats;
  const nextPlan = PLAN_ORDER[PLAN_ORDER.indexOf(plan) + 1];
  const tunesProfiles = meetsPlan(plan, minPlanFor('perfiles'));

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [kind, setKind] = useState<NewAccessKind>(DEFAULT_PROFILE);
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const createKey = useRef(createIdempotencyKeyHolder()).current;

  const createMutation = useMutation({
    mutationFn: () =>
      createAccess(
        kind === 'owner'
          ? { name, email, role: 'owner', temporaryPassword }
          : { name, email, role: 'employee', accessProfile: kind, temporaryPassword },
        createKey.ensure(),
      ),
    meta: { exito: 'Acceso creado' },
    onSuccess: () => {
      setName('');
      setEmail('');
      setKind(DEFAULT_PROFILE);
      setTemporaryPassword('');
      void queryClient.invalidateQueries({ queryKey: ACCESS_QUERY_KEY });
    },
    onSettled: () => createKey.reset(),
  });

  return (
    <>
      <section className="section" aria-label="Accesos">
        <div className="section-head">
          <h2 className="section-label">Accesos</h2>
          {taken !== undefined ? (
            <p className="section-meta" data-full={full || undefined}>
              {taken} de {seats} accesos
            </p>
          ) : null}
        </div>
        {accessQuery.isLoading ? <LoadingState label="Cargando accesos…" /> : null}
        {accessQuery.isError ? <ErrorBanner message={errorMessage(accessQuery.error)} /> : null}
        {accessQuery.data ? (
          accessQuery.data.data.length === 0 ? (
            <EmptyState
              title="Todavía no hay accesos."
              description="Crea uno para que otra persona entre al panel."
            />
          ) : (
            <ul className="rows">
              {accessQuery.data.data.map((access) => (
                <li key={access.id}>
                  <AccessRow access={access} isSelf={access.id === currentUserId} mode={mode} roster={roster} />
                </li>
              ))}
            </ul>
          )
        ) : null}

        <form
          className="card form-grid cols-2"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            createMutation.mutate();
          }}
        >
          <h3 className="section-label">Crear acceso</h3>
          {full ? (
            <div className="seats-full" role="note">
              <p>
                Tu plan {planLabel(plan)} incluye {seats} accesos.{' '}
                {nextPlan && !insideNativeApp() ? `Para sumar más, apaga uno que ya no se use o cambia a ${planLabel(nextPlan)}.` : 'Para sumar más, apaga uno que ya no se use.'}
              </p>
              {nextPlan && !insideNativeApp() ? (
                <Link to="/membresia" search={{ plan: nextPlan }} className="btn small secondary">
                  Cambiar a {planLabel(nextPlan)}
                </Link>
              ) : null}
            </div>
          ) : null}
          <FormField label="Nombre" htmlFor="access-name">
            <input id="access-name" value={name} onChange={(event) => setName(event.target.value)} required />
          </FormField>
          <FormField label="Correo" htmlFor="access-email">
            <input
              id="access-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </FormField>
          <FormField
            label="Perfil"
            htmlFor="access-kind"
            hint={
              kind === 'owner'
                ? 'Ve y maneja todo, igual que tú.'
                : tunesProfiles
                  ? `${PROFILE_BLURB[kind]} Lo ajustas abajo, en Qué ve cada perfil.`
                  : PROFILE_BLURB[kind]
            }
          >
            <select id="access-kind" value={kind} onChange={(event) => setKind(event.target.value as NewAccessKind)}>
              {profilesForMode(mode).map((profile) => (
                <option key={profile} value={profile}>
                  {PROFILE_LABEL[profile]}
                </option>
              ))}
              <option value="owner">{ACCESS_ROLE_LABEL.owner} (ve y maneja todo)</option>
            </select>
          </FormField>
          <FormField label="Contraseña temporal" htmlFor="access-temp-password">
            <input
              id="access-temp-password"
              type="text"
              minLength={8}
              value={temporaryPassword}
              onChange={(event) => setTemporaryPassword(event.target.value)}
              required
            />
          </FormField>
          {createMutation.isError ? <ErrorBanner message={errorMessage(createMutation.error)} /> : null}
          <button type="submit" className="btn" disabled={createMutation.isPending || full}>
            {createMutation.isPending ? 'Creando…' : 'Crear acceso'}
          </button>
        </form>
      </section>
      {tunesProfiles ? <PermisosSection accesses={accessQuery.data?.data} mode={mode} /> : <LockedSection capability="perfiles" />}
    </>
  );
}

type AccessRowProps = {
  access: BusinessAccessRecord;
  isSelf: boolean;
  mode: PortalMode;
  roster: RosterOption[];
};

function AccessRow({ access, isSelf, mode, roster }: AccessRowProps) {
  const queryClient = useQueryClient();
  const [revealedPassword, setRevealedPassword] = useState<string | undefined>();
  const [copied, setCopied] = useState(false);
  const resetKey = useRef(createIdempotencyKeyHolder()).current;
  const toggleKey = useRef(createIdempotencyKeyHolder()).current;

  const resetMutation = useMutation({
    mutationFn: async () => {
      const nextPassword = generateTemporaryPassword();
      await resetAccessPassword(access.id, nextPassword, resetKey.ensure());
      return nextPassword;
    },
    onSuccess: (nextPassword) => {
      setRevealedPassword(nextPassword);
      setCopied(false);
      void queryClient.invalidateQueries({ queryKey: ACCESS_QUERY_KEY });
    },
    onSettled: () => resetKey.reset(),
  });

  const toggleMutation = useMutation({
    mutationFn: () => setAccessActive(access.id, !access.isActive, toggleKey.ensure()),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ACCESS_QUERY_KEY }),
    onSettled: () => toggleKey.reset(),
  });

  const linkKey = useRef(createIdempotencyKeyHolder()).current;
  const linkMutation = useMutation({
    mutationFn: (staffMemberId: string | null) => setAccessStaffMember(access.id, staffMemberId, linkKey.ensure()),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ACCESS_QUERY_KEY }),
    onSettled: () => linkKey.reset(),
  });
  const linkId = `access-agenda-${access.id}`;

  const profileKey = useRef(createIdempotencyKeyHolder()).current;
  const profileMutation = useMutation({
    mutationFn: (profile: AccessProfile) =>
      setAccessProfile(access.id, profile, profileKey.ensure(), {
        unlinkStaff: profile !== 'coach' && Boolean(access.staffMemberId),
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ACCESS_QUERY_KEY }),
    onSettled: () => profileKey.reset(),
  });
  const profileId = `access-profile-${access.id}`;

  const estado = !access.isActive ? 'Desactivado' : access.mustChangePassword ? 'Temporal' : 'Activo';
  const tone = !access.isActive ? 'off' : access.mustChangePassword ? 'hold' : 'good';

  return (
    <div className="row">
      <div className="grow">
        <p className="row-title">{access.name}</p>
        <p className="row-sub">
          {[access.email, accessKindLabel(access)].filter(Boolean).join(' · ')}
        </p>
        {access.username ? <p className="row-sub">Usuario: {access.username}</p> : null}
        <p className="status" data-tone={tone}>
          {estado}
        </p>
        {revealedPassword ? (
          <Notice>
            Contraseña temporal: <strong>{revealedPassword}</strong>{' '}
            <button
              type="button"
              className="linklike"
              onClick={() => {
                void navigator.clipboard.writeText(revealedPassword);
                setCopied(true);
              }}
            >
              {copied ? 'Copiada' : 'Copiar'}
            </button>
          </Notice>
        ) : null}
        {access.role === 'employee' && !isSelf ? (
          <div className="access-agenda">
            <label htmlFor={profileId} className="row-sub">
              Perfil
            </label>
            <select
              id={profileId}
              value={profileOf(access)}
              disabled={profileMutation.isPending}
              onChange={(event) => profileMutation.mutate(event.target.value as AccessProfile)}
            >
              {profilesForMode(mode, profileOf(access)).map((profile) => (
                <option key={profile} value={profile}>
                  {PROFILE_LABEL[profile]}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        {access.role === 'employee' && profileOf(access) === 'coach' && roster.length > 0 ? (
          <div className="access-agenda">
            <label htmlFor={linkId} className="row-sub">
              Ve la agenda de
            </label>
            <select
              id={linkId}
              value={access.staffMemberId ?? ''}
              disabled={linkMutation.isPending}
              onChange={(event) => linkMutation.mutate(event.target.value || null)}
            >
              <option value="">Nadie todavía</option>
              {roster.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        {resetMutation.isError ? <ErrorBanner message={errorMessage(resetMutation.error)} /> : null}
        {toggleMutation.isError ? <ErrorBanner message={errorMessage(toggleMutation.error)} /> : null}
        {linkMutation.isError ? <ErrorBanner message={errorMessage(linkMutation.error)} /> : null}
        {profileMutation.isError ? <ErrorBanner message={errorMessage(profileMutation.error)} /> : null}
      </div>
      <span className="action-pair">
        <ConfirmAction
          label="Restablecer contraseña"
          pending={resetMutation.isPending}
          onConfirm={() => resetMutation.mutate()}
        />
        {isSelf ? null : (
          <ConfirmAction
            label={access.isActive ? 'Desactivar' : 'Activar'}
            pending={toggleMutation.isPending}
            onConfirm={() => toggleMutation.mutate()}
          />
        )}
      </span>
    </div>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos completar eso ahora mismo.';
}
